<template>
  <div>
    <v-card
      flat
      :loading="loading"
      class="tab-details"
    >
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
      ></v-progress-linear>

      <v-card-text>
        <div class="mx-12 text-h6 font-weight-black mt-3 text-c-green">
          {{ local.caseFile.tabHeaders.lawsuitReplyForm }}
        </div>
        <div
          v-if="userCanApplyNewReply"
          class="text-left"
        >
          <naj-button
            type="button"
            @click="validateUserCanApplyNewReply"
          >
            {{ local.caseFile.lawsuitReplyForm.newReply }}
          </naj-button>
        </div>
        <v-row class="mx-12">
          <template v-for="(lawsuitCaseReply, index) in lawsuitCaseReplies">
            <v-col
              :key="index"
              cols="4"
            >
              <lawsuit-case-reply-card
                :lawsuit-case-reply="lawsuitCaseReply"
                :is-pega="isPega"
                @lawsuit-case-reply-card-click="openLawsuitCaseReplyInfo(lawsuitCaseReply)"
              ></lawsuit-case-reply-card>
            </v-col>
          </template>
          <v-col cols="12">
            <naj-no-data
              :has-data="lawsuitCaseReplies && lawsuitCaseReplies.length > 0"
              :loading="loading"
            ></naj-no-data>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <naj-confirmation
      :show-modal="showLawsuitCaseReplyInfoModal"
      :max-width="1500"
      :is-persistent="false"
      :title="`${local.caseFile.lawsuitReplyForm.lawsuitReplyFormInfoModalTitle}`"
      :close-btn-label="local.common.back"
      @cancel="closeLawsuitCaseReplyInfo"
    >
      <template slot="message-content">
        <v-row>
          <v-col
            v-if="selectedLawsuitCaseReply && selectedLawsuitCaseReply.responseReplyNote"
            class="mx-12"
          >
            <div class="text-h6 font-weight-black mt-3 text-c-green">
              {{ local.caseFile.lawsuitReplyForm.applicantInfo.title }} :
            </div>
            <div class="case-info-text mt-5">
              <div>
                {{ local.caseFile.lawsuitReplyForm.applicantInfo.name }} : {{ applicantInfoData.name }}
              </div>

              <div>
                {{ local.caseFile.lawsuitReplyForm.applicantInfo.roleIncase }} : {{ applicantInfoData.roleIncaseName }}
              </div>

              <div v-if="applicantInfoData.isNotProsecutorOrDefendant">
                {{ local.caseFile.lawsuitReplyForm.applicantInfo.onBehalfOfDefendant }} : {{ applicantInfoData.onBehalfOfDefendant }}
              </div>
            </div>

            <naj-item-description
              :title="local.caseFile.lawsuitReplyForm.replyToLawsuit"
              :message="selectedLawsuitCaseReply.responseReplyNote"
            >
            </naj-item-description>
          </v-col>

          <v-col v-else>
            <h1>
              {{ local.caseFile.lawsuitReplyForm.lawsuitReplyFormNoData }}
            </h1>
          </v-col>
        </v-row>
      </template>
    </naj-confirmation>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
const { mapActions } = createNamespacedHelpers('CasesModule');
import LawsuitCaseReplyCard from '../../../../global/components/cards/lawsuit-case-reply-card.vue';
import { casePartyRoleEnum, casePartyRoleByIdEnum } from '@/helpers/enum';

export default {
  name: 'LawsuitCaseRepliesTab',
  components: { LawsuitCaseReplyCard },
  props: {
    caseObjectKey: {
      type: String,
      required: true,
    },
    isPega: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      lawsuitCaseReplies: [],
      loading: false,
      selectedLawsuitCaseReply: null,
      showLawsuitCaseReplyInfoModal: false,
      userCasePartyRoleInCase: {},
    };
  },
  computed: {
    userCanApplyNewReply() {
      return this.userCasePartyRoleInCase.roleInCaseId && this.userCasePartyRoleInCase.roleInCaseId != 0 && this.userCasePartyRoleInCase.roleInCaseId != casePartyRoleEnum.prosecutor;
    },
    applicantInfoData() {
      if (!this.selectedLawsuitCaseReply) {
        return {};
      }

      const isNotProsecutorOrDefendant = this.selectedLawsuitCaseReply.createdByRoleInCaseId != casePartyRoleEnum.prosecutor
        && this.selectedLawsuitCaseReply.createdByRoleInCaseId != casePartyRoleEnum.defendant;

      const casePartyRoleInCaseName = isNotProsecutorOrDefendant ?
        this.local.common.casePartyRoles[casePartyRoleByIdEnum[this.selectedLawsuitCaseReply.createdByRoleInCaseId]]
        : this.local.common.casePartyRoles[casePartyRoleByIdEnum[this.selectedLawsuitCaseReply.casePartyRole]];

      const info = {
        createdByRoleInCaseId: this.selectedLawsuitCaseReply.createdByRoleInCaseId,
        name: isNotProsecutorOrDefendant ? this.selectedLawsuitCaseReply.createdByFullName : this.selectedLawsuitCaseReply.name,
        roleIncaseName: casePartyRoleInCaseName,
        onBehalfOfDefendant: isNotProsecutorOrDefendant ? this.selectedLawsuitCaseReply.name : '',
        isNotProsecutorOrDefendant: isNotProsecutorOrDefendant,
      };
      return info;
    },
  },
  mounted() {
    this.loadData();

    const params = {
      objectKey: this.caseObjectKey,
      isPega: this.isPega,
    };

    this.getUserCasePartyRoleInCase(params)
      .then((res) => {
        this.userCasePartyRoleInCase = res.data;
      })
      .finally(() => (this.loading = false));
  },
  methods: {
    ...mapActions([
      'getLawsuitCaseReplies',
      'getUserCasePartyRoleInCase',
      'validateCaseLawsuitReplyIssuance',
    ]),
    loadData() {
      this.loading = true;
      this.getLawsuitCaseReplies(this.caseObjectKey)
        .then((res) => {
          this.lawsuitCaseReplies = res.data;
        })
        .finally(() => (this.loading = false));
    },
    openLawsuitCaseReplyInfo(lawsuitCaseReply) {
      this.showLawsuitCaseReplyInfoModal = true;
      this.selectedLawsuitCaseReply = lawsuitCaseReply;
    },
    closeLawsuitCaseReplyInfo() {
      this.showLawsuitCaseReplyInfoModal = false;
      this.selectedLawsuitCaseReply = null;
    },
    validateUserCanApplyNewReply() {
      this.loading = true;
      this.validateCaseLawsuitReplyIssuance(this.caseObjectKey)
        .then(() => {
          this.$router.push({ path: `/cases/lawsuit-reply-form/${this.isPega}/${this.caseObjectKey}` });
        })
        .finally(() => (this.loading = false));
    },
  },
};

</script>

<style scoped >
.case-info-text {
  white-space: break-spaces;
  font-size: 16px;
  color: #748aa1;
  font-weight: 400;
  border: 2px solid rgb(221, 221, 221);
  border-radius: 6px;
  background: #fdfdfb 0% 0% no-repeat padding-box;
  padding: 35px 17px 25px 50px;
  overflow-y: auto;
  max-height: 200px;
}

    .text-c-green {
        color: var(--app-primary-color);
    }
</style>